import { format, parseISO, set } from 'date-fns'
import min from 'lodash-es/min'

import { CHARACTER_WHITESPACE, EMPTY_LIST } from '../../constants/common'
import {
  DATE_API_DATA_FORMAT_SHORT,
  DATE_SHORT_FORMAT,
} from '../../constants/date'
import { toUkTimezoneDate } from '../../utils/date'

import { CALENDAR_DAY_LOAD_PENDING } from './CalendarDayLoad.constants'

function getCalendarDayLoadUserDay({ date, load }) {
  return load.days?.find(day => day.date === date)
}

function getCalendarDayLoadApiDate({ timezone, value }) {
  return value
    ? format(toUkTimezoneDate(value, timezone), DATE_API_DATA_FORMAT_SHORT)
    : null
}

function toCalendarDayLoadLines(lines) {
  return lines.length > 0 ? lines.join(CHARACTER_WHITESPACE) : null
}

function toCalendarDayLoadLine({ count, date, limit, name, t, used }) {
  return t('Calendar.CapWarningMassKey', {
    count,
    date: formatCalendarDayLoadDate(date),
    limit,
    used,
    user: name,
  })
}

function toCalendarDayLoadWarningLine({ date, limit, load, t }) {
  const day = getCalendarDayLoadUserDay({ date, load })

  return {
    text: toCalendarDayLoadLine({
      count: day.follow_up_count_after - day.follow_up_count,
      date,
      limit,
      name: load.name,
      t,
      used: day.follow_up_count,
    }),
    userId: load.user_id,
  }
}

export function formatCalendarDayLoadDate(date) {
  return format(parseISO(date), DATE_SHORT_FORMAT)
}

export function getCalendarDayLoadRequestKey({ date, requestIds }) {
  return JSON.stringify({ date, requestIds: [...requestIds].sort() })
}

export function getCalendarDayLoadState({ date, requestKey, response }) {
  return response.requestKey === requestKey
    ? response
    : { ...CALENDAR_DAY_LOAD_PENDING, isLoading: Boolean(date) }
}

export function getCalendarDayLoadCheckedDate({ dueDate, timezone, value }) {
  const date = getCalendarDayLoadApiDate({ timezone, value })

  return date === getCalendarDayLoadApiDate({ timezone, value: dueDate })
    ? null
    : date
}

export function getCalendarDayLoadFullLoads({ data, date }) {
  return data
    ? data.per_user.filter(
        load => getCalendarDayLoadUserDay({ date, load })?.is_full_after
      )
    : EMPTY_LIST
}

export function getCalendarDayLoadNextFreeDate({
  data,
  date,
  fullLoads,
  user,
}) {
  const ownLoad = data?.per_user.find(load => load.user_id === user?.id)

  const nextFreeDate = ownLoad
    ? ownLoad.next_free_date
    : (min(fullLoads.map(load => load.next_free_date).filter(Boolean)) ?? null)

  return nextFreeDate === date ? null : nextFreeDate
}

export function getCalendarDayLoadMovedDate({ date, timezone, value }) {
  const day = parseISO(date)

  return set(toUkTimezoneDate(value, timezone), {
    date: day.getDate(),
    month: day.getMonth(),
    year: day.getFullYear(),
  })
}

export function getCalendarDayLoadWarningLines({ data, date, fullLoads, t }) {
  return fullLoads.length > 0
    ? fullLoads.map(load =>
        toCalendarDayLoadWarningLine({ date, limit: data.limit, load, t })
      )
    : null
}

export function getIsCalendarDayLoadCheckPending(check) {
  return check?.isPending
}

export function getCalendarDayLoadAdvisoryText({ advisory, t }) {
  return toCalendarDayLoadLines(
    advisory.per_user
      .filter(load => load.is_full)
      .map(load =>
        toCalendarDayLoadLine({
          count: load.written_count,
          date: advisory.date,
          limit: advisory.limit,
          name: load.name,
          t,
          used: load.follow_up_count,
        })
      )
  )
}
